import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Calendar, CheckCircle2, Clock, XCircle, Sparkles, AlertTriangle, ShieldAlert, Trash2,
} from "lucide-react";

const STATUS_BADGE = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  cancelled: "bg-slate-50 text-slate-600 border-slate-200",
};

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  return `${dt.getDate()} ${MONTHS[dt.getMonth()]} ${dt.getFullYear()}`;
}
function dayName(d) {
  const dt = new Date(d);
  return dt.toLocaleDateString("en-US", { weekday: "long" });
}
function dateRange(sd, ed) {
  if (!ed || sd === ed) return fmtDate(sd);
  const a = new Date(sd), b = new Date(ed);
  return `${a.getDate()}-${b.getDate()} ${MONTHS[b.getMonth()]} ${b.getFullYear()}`;
}

function HolidayCard({ holiday, mode, quotaRemaining, probation, onApply, onCancel }) {
  const isFixed = holiday.holiday_type === "fixed";
  const app = holiday.my_application;
  const past = holiday.is_past;
  const color = holiday.color || (isFixed ? "#AF3C3C" : "#7c3aed");
  const decided = app && app.status === "approved";

  return (
    <div
      data-testid={`holiday-${holiday.id}`}
      className={`relative bg-white border rounded-xl p-4 transition-all hover:shadow-md ${
        decided ? "border-emerald-300 ring-1 ring-emerald-200" : "border-red-100"
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className="h-12 w-12 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-xl font-bold"
          style={{ background: color }}
        >
          {holiday.name[0]}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-900 truncate">{holiday.name}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            {dateRange(holiday.start_date, holiday.end_date)} · {dayName(holiday.start_date)}
          </div>
          {!isFixed && holiday.description && (
            <div className="text-[11px] text-slate-400 mt-1 italic line-clamp-1">{holiday.description}</div>
          )}
        </div>
      </div>

      {mode === "employee" && !isFixed && (
        <div className="mt-3 pt-3 border-t border-red-50">
          {app ? (
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className={`text-[11px] font-medium border rounded px-2 py-1 inline-flex items-center gap-1 ${STATUS_BADGE[app.status] || ""}`}>
                {app.status === "approved" && <CheckCircle2 className="h-3 w-3" />}
                {app.status === "rejected" && <XCircle className="h-3 w-3" />}
                {app.status === "pending" && <Clock className="h-3 w-3" />}
                {app.status === "cancelled" && <XCircle className="h-3 w-3" />}
                {app.status === "approved" ? "Approved as your optional leave" : `Application ${app.status}`}
              </div>
              {app.status === "pending" && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onCancel(app)}
                  className="h-7 px-2 text-[11px] text-red-600 hover:bg-red-50 hover:text-red-700"
                  data-testid={`cancel-optional-${holiday.id}`}
                >
                  <Trash2 className="h-3 w-3 mr-1" /> Cancel
                </Button>
              )}
            </div>
          ) : past ? (
            <div className="text-[11px] text-slate-400 italic">Date passed — no longer applicable</div>
          ) : !probation?.completed ? (
            <div className="text-[11px] text-amber-700 flex items-center gap-1">
              <ShieldAlert className="h-3 w-3" /> Available after probation ({probation.days_remaining} days)
            </div>
          ) : quotaRemaining <= 0 ? (
            <div className="text-[11px] text-amber-700 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> Quota exhausted (2/2 used this year)
            </div>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onApply(holiday)}
              className="border-red-300 text-red-700 hover:bg-red-50"
              data-testid={`apply-optional-${holiday.id}`}
            >
              <Sparkles className="h-3.5 w-3.5 mr-1.5" />
              Apply for this
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export default function PortalHolidays() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/my-portal/holidays");
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const { fixed, optional } = useMemo(() => {
    const list = data?.holidays || [];
    return {
      fixed: list.filter((h) => h.holiday_type === "fixed"),
      optional: list.filter((h) => h.holiday_type === "optional"),
    };
  }, [data]);

  const submit = async () => {
    if (!selected) return;
    if (reason.trim().length < 5) {
      toast.error("Reason must be at least 5 characters");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/my-portal/optional-leaves/apply", {
        holiday_id: selected.id,
        reason: reason.trim(),
      });
      toast.success("Application submitted. Email sent to your Manager.");
      setSelected(null);
      setReason("");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const cancelApp = async (app) => {
    if (!window.confirm(`Withdraw your request for "${app.holiday_name}"?`)) return;
    try {
      await api.delete(`/my-portal/optional-leaves/${app.id}`);
      toast.success("Your request has been withdrawn.");
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (loading) return <div className="text-sm text-slate-500">Loading...</div>;
  if (!data) return null;

  const quota = data.optional_quota;
  const probation = data.probation;

  return (
    <div className="space-y-6" data-testid="portal-holidays">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Holiday Calendar</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Bravura Holiday Calendar
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Fixed holidays apply automatically. Choose up to 2 Optional holidays per year.
          </p>
        </div>
        <div className="bg-gradient-to-br from-red-50 to-white border border-red-200 rounded-xl px-4 py-3 text-right" data-testid="optional-quota-card">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Optional Leaves {quota.year}</div>
          <div className="text-2xl font-bold brand-text leading-none mt-1">
            {quota.used}<span className="text-slate-300">/{quota.limit}</span>
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            {quota.remaining} remaining
          </div>
        </div>
      </div>

      {!probation.completed && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="probation-banner">
          <ShieldAlert className="h-5 w-5 text-amber-700 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <b>Optional leaves available after probation completion.</b><br />
            Probation ends on <b>{fmtDate(probation.end_date)}</b> ({probation.days_remaining} days remaining).
          </div>
        </div>
      )}

      <section>
        <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-3 flex items-center gap-2">
          <Calendar className="h-4 w-4 brand-text" /> Fixed Holidays (Mandatory)
          <span className="text-xs text-slate-400 font-normal">· {fixed.length} holidays</span>
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="fixed-holidays-grid">
          {fixed.map((h) => (
            <HolidayCard
              key={h.id} holiday={h} mode="employee"
              quotaRemaining={quota.remaining} probation={probation}
              onApply={setSelected} onCancel={cancelApp}
            />
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-slate-900 uppercase tracking-wider mb-3 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-amber-500" /> Optional Holidays (Choose any 2)
          <span className="text-xs text-slate-400 font-normal">· {optional.length} options</span>
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="optional-holidays-grid">
          {optional.map((h) => (
            <HolidayCard
              key={h.id} holiday={h} mode="employee"
              quotaRemaining={quota.remaining} probation={probation}
              onApply={setSelected} onCancel={cancelApp}
            />
          ))}
        </div>
      </section>

      <div className="bg-red-50/40 border border-red-100 rounded-xl p-4 text-xs text-slate-600 space-y-1">
        <div className="font-semibold text-slate-800 mb-1">Notes</div>
        <div>• 2 optional leaves can be availed per year</div>
        <div>• Available after probation completion</div>
        <div>• Apply in advance — past dates not allowed</div>
        <div>• Cannot carry forward to next year</div>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent data-testid="optional-apply-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>Apply for Optional Holiday</DialogTitle>
                <DialogDescription>
                  <span className="block font-medium text-slate-900">{selected.name}</span>
                  <span className="block text-slate-500">
                    {dateRange(selected.start_date, selected.end_date)} · {dayName(selected.start_date)}
                  </span>
                  <span className="block text-xs mt-1">
                    This year used: <b>{quota.used}/{quota.limit}</b>
                  </span>
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                <label className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
                  Reason (required, min 5 chars)
                </label>
                <Textarea
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Why do you want to take this as an optional leave?"
                  data-testid="optional-reason-input"
                />
                <p className="text-[11px] text-slate-500">
                  → Approver: <b>Manager</b> · CC: HOD + Senior Mgmt
                </p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSelected(null)} disabled={submitting}>Cancel</Button>
                <Button onClick={submit} disabled={submitting} className="brand-bg brand-bg-hover text-white"
                        data-testid="optional-submit-btn">
                  {submitting ? "Submitting..." : "Submit for Approval"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
